import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, XCircle, Scissors, Loader2, AlertCircle } from 'lucide-react';

const BASE_URL = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5001';

export default function BookingCancelPage() {
  const { bookingId, token } = useParams();

  const [state, setState] = useState('idle'); // idle | loading | success | error | already | toolate
  const [errorMsg, setErrorMsg] = useState('');

  const handleCancel = async () => {
    setState('loading');
    try {
      await axios.post(`${BASE_URL}/booking/annuler/${bookingId}/${token}`);
      setState('success');
    } catch (err) {
      const status = err.response?.status;
      const msg = err.response?.data?.message || 'Une erreur est survenue.';
      if (status === 409) {
        setState('already');
        setErrorMsg(msg);
      } else if (status === 403) {
        setState('toolate');
        setErrorMsg(msg);
      } else if (status === 404) {
        setState('error');
        setErrorMsg('Ce lien d\'annulation est invalide ou a déjà été utilisé.');
      } else {
        setState('error');
        setErrorMsg(msg);
      }
    }
  };

  // ── Success ──
  if (state === 'success') {
    return (
      <Screen>
        <div className="w-20 h-20 bg-emerald-50 rounded-full flex items-center justify-center">
          <CheckCircle size={44} className="text-emerald-500" />
        </div>
        <div className="text-center">
          <h1 className="text-2xl font-black text-slate-900">Rendez-vous annulé</h1>
          <p className="text-slate-500 text-sm mt-2">
            Votre rendez-vous a bien été annulé. Vous pouvez reprendre un nouveau créneau à tout moment.
          </p>
        </div>
      </Screen>
    );
  }

  // ── Warning states (already cancelled/finished, or too close to appointment) ──
  if (state === 'already' || state === 'toolate') {
    return (
      <Screen>
        <div className="w-20 h-20 bg-orange-50 rounded-full flex items-center justify-center">
          <AlertCircle size={44} className="text-orange-400" />
        </div>
        <div className="text-center">
          <h1 className="text-2xl font-black text-slate-900">
            {state === 'already' ? 'Déjà traité' : 'Annulation impossible'}
          </h1>
          <p className="text-slate-500 text-sm mt-2">{errorMsg}</p>
        </div>
      </Screen>
    );
  }

  // ── Invalid link ──
  if (state === 'error') {
    return (
      <Screen>
        <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center">
          <XCircle size={44} className="text-red-400" />
        </div>
        <div className="text-center">
          <h1 className="text-2xl font-black text-slate-900">Lien invalide</h1>
          <p className="text-slate-500 text-sm mt-2">{errorMsg}</p>
        </div>
      </Screen>
    );
  }

  // ── Idle / Loading ──
  return (
    <Screen>
      <div className="w-20 h-20 bg-blue-50 rounded-full flex items-center justify-center">
        <Scissors size={36} className="text-blue-500" />
      </div>
      <div className="text-center">
        <h1 className="text-2xl font-black text-slate-900">Annuler mon rendez-vous</h1>
        <p className="text-slate-500 text-sm mt-2">
          Voulez-vous vraiment annuler ce rendez-vous ? Cette action est irréversible.
        </p>
      </div>
      <button
        onClick={handleCancel}
        disabled={state === 'loading'}
        className="w-full max-w-xs py-4 bg-red-500 text-white rounded-2xl font-black text-sm hover:bg-red-600 transition-all disabled:opacity-60 flex items-center justify-center gap-2 shadow-lg shadow-red-100"
      >
        {state === 'loading'
          ? <><Loader2 size={18} className="animate-spin" /> Annulation en cours…</>
          : <><XCircle size={18} /> Confirmer l'annulation</>
        }
      </button>
    </Screen>
  );
}

function Screen({ children }) {
  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center px-4 py-12">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-sm border border-slate-100 p-8 flex flex-col items-center gap-6">
        {children}
      </div>
    </div>
  );
}
